import type { ReaderBridge } from './types.js'

// ---------------------------------------------------------------------------
// HIGHLIGHT_SCRIPT_SOURCE
//
// Single source of truth: the vanilla JavaScript that paints highlights,
// reads the current selection and exposes the `window.arianna*` API.
//
// This SAME string is:
//   1. embedded inside the IIFE that ships with the mobile WebView HTML
//      (`buildBridgeJs` below), and
//   2. injected as a <script> in the host DOM by `highlightDom.ts` for
//      web/desktop consumers (React DOM hooks).
//
// Keep it pure vanilla JS (no TS syntax, no imports, no ES module features)
// so that it executes byte-identical on iOS/macOS WKWebView, Chrome V8 and
// Tauri's WKWebView. `Function.prototype.toString()` was previously used to
// stringify TS-compiled helpers but that breaks under Hermes/Metro (the
// returned source carries references to internal `bytecode` symbols that do
// not exist outside the Hermes runtime).
// ---------------------------------------------------------------------------

export const HIGHLIGHT_SCRIPT_SOURCE = `
function ariannaNfc(s) { return s.normalize('NFC'); }

function ariannaTextPosition(root, offset) {
  var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  var current = 0;
  var node;
  while ((node = walker.nextNode())) {
    var next = current + ariannaNfc(node.nodeValue).length;
    if (offset <= next) {
      return { node: node, offset: Math.max(0, offset - current) };
    }
    current = next;
  }
  return null;
}

function ariannaCreateHighlightSpan(h, style) {
  var span = document.createElement('span');
  span.className = 'hl hl-' + style + ' hl-' + h.color;
  span.dataset.highlightId = h.id;
  span.dataset.highlightStyle = style;
  return span;
}

function ariannaWrapTextSegment(node, startOffset, endOffset, h, style) {
  if (!node || !node.parentNode || startOffset >= endOffset) return;
  var text = node.nodeValue || '';
  if (endOffset > text.length) return;

  if (endOffset < text.length) node.splitText(endOffset);
  var selected = startOffset > 0 ? node.splitText(startOffset) : node;
  var parent = selected.parentNode;
  if (!parent) return;

  var span = ariannaCreateHighlightSpan(h, style);
  parent.insertBefore(span, selected);
  span.appendChild(selected);
}

function ariannaApplyHighlight(h) {
  var escapeCss = window.CSS && window.CSS.escape
    ? window.CSS.escape
    : function (value) { return String(value).replace(/["\\\\]/g, '\\\\$&'); };
  var subParaScope = document.querySelector('[data-sub-para-id="' + escapeCss(h.subParaId) + '"]');
  var block;
  if (subParaScope && subParaScope.dataset.blockId === h.blockId) {
    block = subParaScope;
  } else {
    block = document.querySelector('[data-block-id="' + escapeCss(h.blockId) + '"]');
  }
  if (!block || h.startChar >= h.endChar || ariannaNfc(block.textContent).length < h.endChar) return;
  var style = h.style === 'underline' ? 'underline' : 'mark';
  var walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  var current = 0;
  var node;
  var segments = [];
  while ((node = walker.nextNode())) {
    var length = ariannaNfc(node.nodeValue).length;
    var next = current + length;
    var start = Math.max(h.startChar, current);
    var end = Math.min(h.endChar, next);
    if (start < end) {
      segments.push({ node: node, start: start - current, end: end - current });
    }
    current = next;
    if (current >= h.endChar) break;
  }
  for (var i = segments.length - 1; i >= 0; i--) {
    ariannaWrapTextSegment(segments[i].node, segments[i].start, segments[i].end, h, style);
  }
}

function ariannaClearHighlights(root) {
  var scope = root || document;
  var spans = scope.querySelectorAll('.hl');
  for (var i = 0; i < spans.length; i++) {
    var span = spans[i];
    var parent = span.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    if (parent.normalize) parent.normalize();
  }
}

function ariannaSetHighlightsImpl(highlights) {
  ariannaClearHighlights();
  for (var i = 0; i < highlights.length; i++) ariannaApplyHighlight(highlights[i]);
}

function ariannaClosestBlock(node) {
  if (!node) return null;
  var el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
  return el && el.closest ? el.closest('[data-block-id]') : null;
}

function ariannaSelectedRange() {
  var selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return null;
  var range = selection.getRangeAt(0);
  var block = ariannaClosestBlock(range.commonAncestorContainer);
  if (!block) return null;
  if (!block.contains(range.startContainer) || !block.contains(range.endContainer)) return null;
  var before = document.createRange();
  before.selectNodeContents(block);
  before.setEnd(range.startContainer, range.startOffset);
  var selected = ariannaNfc(range.toString());
  var startChar = ariannaNfc(before.toString()).length;
  var endChar = startChar + selected.length;
  if (selected.trim().length === 0 || startChar === endChar) return null;
  return {
    type: 'selection',
    subParaId: block.dataset.subParaId || '',
    blockId: block.dataset.blockId || '',
    startChar: startChar,
    endChar: endChar,
    text: selected.slice(0, 160)
  };
}

window.ariannaNfc = ariannaNfc;
window.ariannaTextPosition = ariannaTextPosition;
window.ariannaClosestBlock = ariannaClosestBlock;
window.ariannaApplyHighlight = ariannaApplyHighlight;
window.ariannaClearHighlights = ariannaClearHighlights;
window.ariannaSelectedRange = ariannaSelectedRange;
window.ariannaSetHighlights = function (highlights) { ariannaSetHighlightsImpl(highlights || []); };
window.ariannaSetTheme = function (theme) {
  if (theme !== 'studiolo' && theme !== 'notturno') return;
  document.documentElement.setAttribute('data-theme', theme);
};
`

// ---------------------------------------------------------------------------
// buildBridgeJs
//
// Wraps HIGHLIGHT_SCRIPT_SOURCE in an IIFE that also installs the
// WebView-specific machinery: the `post` channel back to the host, the
// selection/touch listeners and the initial 'ready' signal.
//
// The `debug` option (default: false) enables postMessage events with
// type:'debug' for diagnosing selection/timing issues from the host's
// Metro/console output.
// ---------------------------------------------------------------------------

export function buildBridgeJs(
  bridge: ReaderBridge,
  opts?: { debug?: boolean; blockActions?: boolean },
): string {
  const debug = opts?.debug === true
  const blockActions = opts?.blockActions !== false
  return `
(function () {
  function post(payload) {
    ${bridge.postExpression};
  }
  function debugLog(subtype, extra) {
    ${debug ? "post(Object.assign({ type: 'debug', subtype: subtype }, extra || {}));" : ''}
  }

  ${HIGHLIGHT_SCRIPT_SOURCE}

  var selectionTimer = null;
  function fireSelectionPayload(source) {
    try {
      var payload = window.ariannaSelectedRange();
      debugLog('selection-result', { hasPayload: !!payload, source: source });
      post(payload || { type: 'selection-cleared' });
    } catch (e) {
      debugLog('selection-error', {
        source: source,
        error: String((e && e.message) || e),
        stack: String((e && e.stack) || '')
      });
    }
  }
  function handleSelectionEvent(source) {
    debugLog('selection-event', { source: source });
    if (source === 'pointerup' || source === 'touchend' || source === 'mouseup') {
      if (selectionTimer != null) { clearTimeout(selectionTimer); selectionTimer = null; }
      fireSelectionPayload(source);
      return;
    }
    if (selectionTimer != null) clearTimeout(selectionTimer);
    selectionTimer = setTimeout(function () {
      selectionTimer = null;
      fireSelectionPayload('selectionchange');
    }, 150);
  }
  document.addEventListener('selectionchange', function () { handleSelectionEvent('selectionchange'); });
  document.addEventListener('touchend', function () { handleSelectionEvent('touchend'); }, { passive: true });
  document.addEventListener('mouseup', function () { handleSelectionEvent('mouseup'); });
  document.addEventListener('pointerup', function () { handleSelectionEvent('pointerup'); });

  var scrollTimer = null;
  window.addEventListener('scroll', function () {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(function () {
      var doc = document.documentElement;
      var max = Math.max(1, doc.scrollHeight - window.innerHeight);
      var payload = { type: 'scroll', ratio: Math.min(1, Math.max(0, window.scrollY / max)) };
      post(payload);
    }, 120);
  }, { passive: true });

  ${blockActions ? BLOCK_ACTION_SCRIPT : ''}

  window.ariannaSetHighlights(window.__ARIANNA_HIGHLIGHTS__ || []);
  post({ type: 'ready', blockCount: document.querySelectorAll('[data-block-id]').length });
})();
`
}

// ---------------------------------------------------------------------------
// BLOCK_ACTION_SCRIPT
//
// Margin-glyph affordance for the mobile reader (Fase 8b, contratto "Mobile
// — Micro-menu" rivisto). Il long-press custom della Fase 8 è stato scartato:
// su iOS il long-press sul TESTO attiva la selezione di sistema con il
// callout copia/incolla, che va preservata — sopprimerla per far posto al
// nostro menu rompeva quel comportamento nativo.
//
// Al suo posto: ogni blocco (`[data-block-id]`) espone un glifo `✦` figlio
// nel margine sinistro (`.arianna-block-action`, marcato `data-block-action`,
// renderizzato da html.ts e stilizzato in css.ts). Un tap SUL GLIFO — mai sul
// testo — emette `{ type: 'block-action', subParaId, blockId, rect }`, dove
// `rect` è il getBoundingClientRect() del BLOCCO (non del glifo) in
// coordinate viewport (CSS px). La copia della selezione di testo resta
// interamente quella nativa di iOS: questo script non la tocca mai.
//
// Trigger: pointerdown/touchstart SUL GLIFO con preventDefault (impedisce che
// il tap avvii una selezione di testo) → pointerup/touchend con movimento
// euclideo ≤ 10px dal punto di partenza E nessuna selezione di testo attiva.
// Usa PointerEvent quando disponibile (WKWebView iOS/macOS lo supporta),
// altrimenti Touch Events — mai entrambi assieme, per non far scattare due
// gestori per lo stesso gesto.
// ---------------------------------------------------------------------------

const BLOCK_ACTION_SCRIPT = `
  (function () {
    var MOVE_TOLERANCE_PX = 10;

    var activeGlyph = null;
    var startX = 0;
    var startY = 0;

    function pointFromEvent(event) {
      if (event.changedTouches && event.changedTouches.length > 0) {
        return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
      }
      if (event.touches && event.touches.length > 0) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    }

    function closestGlyph(node) {
      if (!node) return null;
      var el = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      return el && el.closest ? el.closest('[data-block-action]') : null;
    }

    function handleStart(event) {
      var glyph = closestGlyph(event.target);
      if (!glyph) { activeGlyph = null; return; }
      // Prevents the tap from also starting a native text selection on the
      // glyph itself — the selection flow (and its native copy/paste
      // callout) is preserved everywhere else in the block.
      event.preventDefault();
      activeGlyph = glyph;
      var point = pointFromEvent(event);
      startX = point.x;
      startY = point.y;
    }

    function handleEnd(event) {
      var glyph = activeGlyph;
      activeGlyph = null;
      if (!glyph) return;
      var point = pointFromEvent(event);
      var dx = point.x - startX;
      var dy = point.y - startY;
      if (Math.sqrt(dx * dx + dy * dy) > MOVE_TOLERANCE_PX) return;
      var selection = window.getSelection();
      if (selection && !selection.isCollapsed && selection.toString().length > 0) return;
      var block = window.ariannaClosestBlock(glyph);
      if (!block) return;
      var rect = block.getBoundingClientRect();
      post({
        type: 'block-action',
        subParaId: block.dataset.subParaId || '',
        blockId: block.dataset.blockId || '',
        rect: { x: rect.x, y: rect.y, width: rect.width, height: rect.height }
      });
    }

    function handleCancel() {
      activeGlyph = null;
    }

    var usePointerEvents = typeof window.PointerEvent !== 'undefined';
    if (usePointerEvents) {
      document.addEventListener('pointerdown', handleStart, { passive: false });
      document.addEventListener('pointerup', handleEnd, { passive: true });
      document.addEventListener('pointercancel', handleCancel, { passive: true });
    } else {
      document.addEventListener('touchstart', handleStart, { passive: false });
      document.addEventListener('touchend', handleEnd, { passive: true });
      document.addEventListener('touchcancel', handleCancel, { passive: true });
    }
  })();
`
